import Immutable from 'immutable';

import ErrorData from 'sharedreact/data/ErrorData';
import type Filter from 'sharedreact/data/Filter';
import type GroupAndCount from 'sharedreact/data/GroupAndCount';
import type SortOrder from 'sharedreact/data/SortOrder';
import UploadFileResult from 'sharedreact/data/UploadFileResult';
import UploadedFile from 'sharedreact/data/UploadedFile';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import type FontFile from 'workspaces/launcher/data/FontFile';

import FontsConnection from 'workspaces/launcher/connections/FontsConnection';
import FontsConstants from 'workspaces/launcher/constants/FontsConstants';

function _setConfirmOverwrite (needsConfirmOverwrite: boolean, uploadedFiles: Immutable.List<UploadedFile> | File[]) {
  AppDispatcher.dispatch({
    actionType: FontsConstants.SET_CONFIRM_OVERWRITE,
    needsConfirmOverwrite: needsConfirmOverwrite,
    files: uploadedFiles,
    logLevel: 'trace'
  });
}

function _onUploadSuccess (uploadFileResult: UploadFileResult): void {
  if (uploadFileResult.status === UploadFileResult.Status.OK) {
    AppDispatcher.dispatch({
      actionType: FontsConstants.REFRESH,
      logLevel: 'trace'
    });
  }
}

function _onUploadFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.UPLOAD_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function _clearConfirmOverwrite () {
  AppDispatcher.dispatch({
    actionType: FontsConstants.SET_CONFIRM_OVERWRITE,
    needsConfirmOverwrite: false,
    files: Immutable.List(),
    logLevel: 'trace'
  });
}

function uploadFonts (files: FileList | File[] | Immutable.List<UploadedFile>, forceOverwrite: boolean = false): void {
  if (!forceOverwrite && (files instanceof FileList || Array.isArray(files))) {
    // if we don't force overwrite, check if files are already present on server first
    // if true, the function will be called again with forceOverwrite set to true

    let fileList: File[] = []; // we need to create a new list of files so that those files don't get closed when this function returns
    let fileNames: string[] = [];
    for (const file of files) {
      fileNames.push(file.name);
      fileList.push(file);
    }

    FontsConnection.checkIfNeedsOverwrite(
      fileNames,
      (confirmOverwrite) => {
        if (confirmOverwrite) {
          _setConfirmOverwrite(confirmOverwrite, fileList);
        } else {
          FontsConnection.uploadFonts(files, forceOverwrite, _onUploadSuccess, _onUploadFailure);
          _clearConfirmOverwrite();
        }
      },
      _onUploadFailure
    );
  } else {
    FontsConnection.uploadFonts(files, forceOverwrite, _onUploadSuccess, _onUploadFailure);
    _clearConfirmOverwrite();
  }
}

function cancelOverwrite (files: FileList | File[] |  Immutable.List<UploadedFile>): void {
  _clearConfirmOverwrite();
}

function _fetchSuccess (
  range: RangeType,
  totalCount: number,
  items: Immutable.List<FontFile>,
  countPerGroup: Immutable.List<GroupAndCount>,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder
): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.GET_SUCCESS,
    range: range,
    totalCount: totalCount,
    items: items,
    countPerGroup: countPerGroup,
    filter: filter,
    advancedFilter: advancedFilter,
    groupBy: groupBy,
    sortOrder: sortOrder,
    logLevel: 'debug'
  });
}

function _fetchFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.GET_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function fetch (
  range: RangeType,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder,
  isInitialFetch: boolean
): void {
  FontsConnection.fetch(
    range,
    filter,
    advancedFilter,
    groupBy,
    sortOrder,
    isInitialFetch,
    _fetchSuccess,
    _fetchFailure
  );
}

function setVisibleRange (range: RangeType): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.SET_VISIBLE_RANGE,
    range: range,
    logLevel: 'trace'
  });
}

function setSortOrder (sortOrder: SortOrder): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.SET_SORTORDER,
    sortOrder: sortOrder,
    logLevel: 'trace'
  });
}

function activateFilter (filterMode: string): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.ACTIVATE_FILTER,
    filterMode: filterMode,
    logLevel: 'trace'
  });
}

function setFilter (filterMode: string, filter: Filter, filterString: string): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.SET_FILTER,
    filterMode: filterMode,
    filter: filter,
    filterString: filterString,
    logLevel: 'trace'
  });
}

function refresh (): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.REFRESH,
    logLevel: 'trace'
  });
}

function setSelection (selection: Immutable.Set<string>): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.SET_SELECTION,
    selection: selection,
    logLevel: 'trace'
  });
}

function modifySelection (id: string): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.MODIFY_SELECTION,
    id: id,
    logLevel: 'trace'
  });
}

function allowMultipleSelections (allow: boolean): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.ALLOW_MULTI_SELECT,
    allow: allow,
    logLevel: 'trace'
  });
}

function _onRemoveSuccess () {
  AppDispatcher.dispatch({
    actionType: FontsConstants.REFRESH,
    logLevel: 'trace'
  });
}

function _onRemoveFailure (error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: FontsConstants.REMOVE_FAIL,
    error: error,
    logLevel: 'error'
  });
}

function remove (ids: Immutable.Set<string>): void {
  FontsConnection.remove(ids, _onRemoveSuccess, _onRemoveFailure);
}

export default {
  uploadFonts,
  fetch,
  setVisibleRange,
  setSortOrder,
  setFilter,
  activateFilter,
  refresh,
  setSelection,
  modifySelection,
  allowMultipleSelections,
  remove,
  cancelOverwrite
};
